// kore's AuditSheet plus the three fetches it needs: the focused event, its
// target's activity, and its caused-by parent. Shared by the audit page
// (URL-driven) and HistorySection (local state). kore owns the back stack;
// hosts only hold the focused id.

import { AuditSheet } from "@pej/kore/components";
import type { AuditEvent } from "@pej/kore/types";
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { useAuditEvent, useAuditSearch } from "@/api/audit";
import type { AuditTargetType } from "@/api/types";
import { targetTimelineRq } from "./audit-filters";
import {
    auditActionMeta,
    auditDetailRenderers,
    auditTargetTypeLabel,
    summarizeKoreEvent,
    toKoreAuditEvent,
} from "./kore-audit-adapter";

export interface AuditEventSheetProps {
    workspaceId: string;
    /** Focused event; `null` closes the sheet. */
    eventId: string | null;
    onNavigate: (eventId: string) => void;
    onClose: () => void;
    /** Fired once the focused id resolves to a 404. */
    onNotFound?: () => void;
    renderMetadata?: (event: AuditEvent) => ReactNode;
}

export function AuditEventSheet({
    workspaceId,
    eventId,
    onNavigate,
    onClose,
    onNotFound,
    renderMetadata,
}: AuditEventSheetProps) {
    const focusedQuery = useAuditEvent(workspaceId, eventId);
    const focusedRaw = focusedQuery.data ?? null;
    const focused = useMemo(() => (focusedRaw ? toKoreAuditEvent(focusedRaw) : null), [focusedRaw]);

    // Keep the last event through the close animation.
    const lastEventRef = useRef<AuditEvent | null>(null);
    if (focused) lastEventRef.current = focused;
    const sheetEvent = eventId === null ? lastEventRef.current : focused;

    const target = focusedRaw?.target ?? null;
    // The server only records its own target types.
    const timelineRq = targetTimelineRq((target?.type ?? "") as AuditTargetType, target?.id ?? "");
    const timelineQuery = useAuditSearch(workspaceId, timelineRq, {
        enabled: target !== null,
        keepPrevious: false,
    });
    const timeline = useMemo(() => (timelineQuery.data?.items ?? []).map(toKoreAuditEvent), [timelineQuery.data]);

    const causedByQuery = useAuditEvent(workspaceId, focusedRaw?.causedByEventId ?? null);
    const causedByEvent = useMemo(
        () => (causedByQuery.data ? toKoreAuditEvent(causedByQuery.data) : null),
        [causedByQuery.data],
    );

    const notFound = eventId !== null && focusedQuery.isSuccess && focusedQuery.data === null;
    useEffect(() => {
        if (notFound) onNotFound?.();
    }, [notFound, onNotFound]);

    return (
        <AuditSheet
            open={eventId !== null && !notFound}
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
            event={sheetEvent}
            timeline={timeline}
            timelineLoading={timelineQuery.isLoading}
            causedByEvent={causedByEvent}
            causedByLoading={causedByQuery.isLoading}
            onNavigate={onNavigate}
            detailRenderers={auditDetailRenderers}
            actionMeta={auditActionMeta}
            summarize={summarizeKoreEvent}
            targetTypeLabel={auditTargetTypeLabel}
            renderMetadata={renderMetadata}
        />
    );
}
